import { z } from 'zod';
import {
    ValidationEntrySeverity,
    ValidationEntrySeveritySchema,
} from '../../../../../react-commons/components/ValidationEntrySeverity';
import { ResourceType, ResourceTypeSchema } from '../../enumerations/ResourceType';
import { msObject } from '../../resource-base/msObject';
import { CompoundValidationItem, CompoundValidationItemSchema, ValidationItem, ValidationItemSchema } from './types';

const ValidationResultSchema = msObject({
    propertyValidation: ValidationItemSchema.array(),
    generalValidation: CompoundValidationItemSchema.array(),
    childValidation: ValidationItemSchema.array(),
});

export const ValidationResourceSchema = msObject({
    CRITICAL: ValidationResultSchema.optional(),
    WARNING: ValidationResultSchema.optional(),
    NOTE: ValidationResultSchema.optional(),
    FATAL: ValidationResultSchema.optional(),
});

export const transformToDetailedValidationResourceSchema = (
    validationResourceSchemaWithResourceType: ValidationResource & { resourceType: ResourceType }
) => {
    const propertyValidationMapping: Record<string, { severity: ValidationEntrySeverity; identifier: string }> = {};

    const processPropertyValidation = (value: ValidationItem, severity: ValidationEntrySeverity) => {
        if (value.propertyName) {
            const existingItem = propertyValidationMapping[value.propertyName];
            const identifier = existingItem ? `${existingItem.identifier}, ${value.identifier}` : value.identifier;
            propertyValidationMapping[value.propertyName] = { severity, identifier };
        }
    };
    const processGeneralValidation = (value: CompoundValidationItem, severity: ValidationEntrySeverity) => {
        if (value.properties) {
            value.properties.forEach((propertyName) => {
                const existingItem = propertyValidationMapping[propertyName];
                const identifier = existingItem ? `${existingItem.identifier}, ${value.identifier}` : value.identifier;
                propertyValidationMapping[propertyName] = { severity, identifier };
            });
        }
    };

    validationResourceSchemaWithResourceType.NOTE?.propertyValidation.forEach((value) =>
        processPropertyValidation(value, ValidationEntrySeverity.NOTE)
    );
    validationResourceSchemaWithResourceType.NOTE?.generalValidation.forEach((value) =>
        processGeneralValidation(value, ValidationEntrySeverity.NOTE)
    );
    validationResourceSchemaWithResourceType.WARNING?.propertyValidation.forEach((value) =>
        processPropertyValidation(value, ValidationEntrySeverity.WARNING)
    );
    validationResourceSchemaWithResourceType.WARNING?.generalValidation.forEach((value) =>
        processGeneralValidation(value, ValidationEntrySeverity.WARNING)
    );
    validationResourceSchemaWithResourceType.CRITICAL?.propertyValidation.forEach((value) =>
        processPropertyValidation(value, ValidationEntrySeverity.CRITICAL)
    );
    validationResourceSchemaWithResourceType.CRITICAL?.generalValidation.forEach((value) =>
        processGeneralValidation(value, ValidationEntrySeverity.CRITICAL)
    );
    validationResourceSchemaWithResourceType.FATAL?.propertyValidation.forEach((value) =>
        processPropertyValidation(value, ValidationEntrySeverity.FATAL)
    );
    validationResourceSchemaWithResourceType.FATAL?.generalValidation.forEach((value) =>
        processGeneralValidation(value, ValidationEntrySeverity.FATAL)
    );

    const fatalCount =
        validationResourceSchemaWithResourceType.FATAL ?
            validationResourceSchemaWithResourceType.FATAL.propertyValidation.length +
            validationResourceSchemaWithResourceType.FATAL.generalValidation.length +
            validationResourceSchemaWithResourceType.FATAL.childValidation.length
        :   0;
    const errorCount =
        validationResourceSchemaWithResourceType.CRITICAL ?
            validationResourceSchemaWithResourceType.CRITICAL.propertyValidation.length +
            validationResourceSchemaWithResourceType.CRITICAL.generalValidation.length +
            validationResourceSchemaWithResourceType.CRITICAL.childValidation.length
        :   0;
    const warningCount =
        validationResourceSchemaWithResourceType.WARNING ?
            validationResourceSchemaWithResourceType.WARNING.propertyValidation.length +
            validationResourceSchemaWithResourceType.WARNING.generalValidation.length +
            validationResourceSchemaWithResourceType.WARNING.childValidation.length
        :   0;
    const noteCount =
        validationResourceSchemaWithResourceType.NOTE ?
            validationResourceSchemaWithResourceType.NOTE.propertyValidation.length +
            validationResourceSchemaWithResourceType.NOTE.generalValidation.length +
            validationResourceSchemaWithResourceType.NOTE.childValidation.length
        :   0;

    const maxSeverity =
        fatalCount > 0 ? ValidationEntrySeverity.FATAL
        : errorCount > 0 ? ValidationEntrySeverity.CRITICAL
        : warningCount > 0 ? ValidationEntrySeverity.WARNING
        : noteCount > 0 ? ValidationEntrySeverity.NOTE
        : undefined;
    const summary = `Fatals - ${fatalCount}, Criticals - ${errorCount}, Warnings - ${warningCount}, Notes - ${noteCount}`;

    return { ...validationResourceSchemaWithResourceType, propertyValidationMapping, maxSeverity, summary };
};

export const DetailedValidationResourceSchema = ValidationResourceSchema.extend({
    propertyValidationMapping: z.record(z.string(), msObject({ severity: ValidationEntrySeveritySchema, identifier: z.string() })),
    maxSeverity: ValidationEntrySeveritySchema.optional(),
    summary: z.string().min(50),
    resourceType: ResourceTypeSchema,
});

export type ValidationResource = z.infer<typeof ValidationResourceSchema>;
export type DetailedValidationResource = z.infer<typeof DetailedValidationResourceSchema>;
